import {
  combineReducers,
  configureStore,
  createListenerMiddleware,
  isAnyOf,
  isRejected,
  isRejectedWithValue,
} from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { apis } from "./api";
import { middlewares } from "./middlewares";
import { slices } from "./slices";

/*
Dispatch Pipeline:
dispatch(action)
  └─> listener.middleware
        └─> api1.middleware
              └─> api2.middleware
                    …etc…
                      └─> errorMiddleware
                            └─> loggerMiddleware
                                  └─> authMiddleware
                                        └─> serverTimingMiddleware
                                              └─> rootReducer
*/

const apiReducers = Object.fromEntries(
  apis.map((api) => [api.reducerPath, api.reducer])
);

middlewares.unshift(...apis.map((api) => api.middleware));

const baseReducer = combineReducers({
  ...slices,
  ...apiReducers,
});

const rootReducer = (state: any, action: any) => {
  if (action.type === "RESET_APP") {
    state = undefined;
  }
  return baseReducer(state, action);
};

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth", "project", "release"], // Only persist auth, project, and release slices
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const listener = createListenerMiddleware();

listener.startListening({
  matcher: isAnyOf(isRejected(), isRejectedWithValue()),
  effect: (action: any) => {
    if (
      action.meta?.aborted ||
      action.meta?.condition ||
      action.error?.name === "ConditionError" ||
      action.error?.name === "AbortError"
    ) {
      return;
    }
    console.error("Error:", action);
  },
});
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false, // Disable ImmutableStateInvariantMiddleware
    })
      .prepend(listener.middleware)
      .concat(middlewares),
  // only enable devtools in development:
  devTools: import.meta.env.DEV
    ? {
        name: "Karas",
        trace: false,
        traceLimit: 0,
        serialize: false,
        maxAge: 5,
      }
    : false,
});

export const persistor = persistStore(store);

// Inferred types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export default store;
